import {createAsyncThunk, createSlice, type PayloadAction} from "@reduxjs/toolkit";
import type {AuctionBid, CarAuction, ListingFilters} from "@/types";
import {fallbackDealer, type RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {keysToCamelCase, normalizeError} from "@/utils";
import {createVehicleThunk} from "@/store/thunks/createVehicleThunk.ts";

export let mockBidInterval: ReturnType<typeof setInterval> | null = null;

const PAGE_SIZE = 20;

interface AuctionState {
    loading: boolean;
    error?: string;
    hasError: boolean;
    errorMessage?: string;
    isSubscribed: boolean;

    auctions: CarAuction[];
    latestAuction?: CarAuction;
    latestAuctionFetched: boolean;
    currentAuction?: CarAuction;
    fetched: boolean;
    pageSize: number;
    pageNumber: number;
    pages: number;
    total: number;
    fetchedPages: number[];
    filters: ListingFilters;
}

const initialState: AuctionState = {
    fetched: false,
    loading: false,
    hasError: false,
    error: undefined,
    errorMessage: undefined,
    isSubscribed: false,
    auctions: [],
    latestAuctionFetched: false,
    pageSize: PAGE_SIZE,
    pageNumber: 1,
    pages: 0,
    total: 0,
    fetchedPages: [],
    filters: {}
};

function applyFilters(listings: CarAuction[], filters: ListingFilters): CarAuction[] {
    return listings.filter((item) => {
        if (filters.brand && item.brand.toLowerCase() !== filters.brand.toLowerCase()) return false;
        if (filters.model && item.model.toLowerCase() !== filters.model.toLowerCase()) return false;
        if (filters.body && item.body?.toLowerCase() !== filters.body.toLowerCase()) return false;
        if (filters.transmission && item.transmission !== filters.transmission) return false;
        if (typeof filters.minPrice === "number" && item.startingBid < filters.minPrice) return false;
        if (typeof filters.maxPrice === "number" && item.startingBid > filters.maxPrice) return false;

        if (filters.query) {
            const q = filters.query.toLowerCase();
            const haystack = `${item.year} ${item.brand} ${item.model} ${item.name}`.toLowerCase();
            if (!haystack.includes(q)) return false;
        }

        return true;
    });
}

function mapDbAuctionToCarAuction(row: any, currentUser: RootState["authentication"]["user"]): CarAuction {
    const vehicle = keysToCamelCase(row?.vehicle ?? {}) as Record<string, any>;
    const auctionData = keysToCamelCase(row ?? {}) as Record<string, any>;
    const seller = currentUser ?? fallbackDealer();

    return {
        ...vehicle,
        id: String(vehicle.id ?? auctionData.vehicleId ?? auctionData.id ?? ""),
        auctionId: String(auctionData.id ?? ""),
        type: "auction",
        seller,
        sellerId: String(vehicle.sellerId ?? auctionData.sellerId ?? ""),
        startingBid: Number(auctionData.startingBid ?? 0),
        currentBid: Number(auctionData.currentBid ?? auctionData.startingBid ?? 0),
        ending: auctionData.ending ? String(auctionData.ending) : new Date().toISOString(),
        bids: Array.isArray(auctionData.bids) ? auctionData.bids : [],
        totalBids: Number(auctionData.totalBids ?? 0),
        active: vehicle.active ?? "live",
        createdAt: String(vehicle.createdAt ?? auctionData.createdAt ?? new Date().toISOString())
    } as CarAuction;
}

function setErrorState(state: AuctionState, message?: string) {
    state.hasError = Boolean(message);
    state.errorMessage = message;
    state.error = message;
}

export const fetchAuctionsAsync = createAsyncThunk<
    { listings: CarAuction[]; page: number; totalPages: number; total: number },
    number | undefined,
    { state: RootState; rejectValue: string }
>("auction/fetchAuction", async (page, {getState, rejectWithValue}) => {
    const state = getState();
    const {fetchedPages, filters} = state.auction;
    const currentUser = state.authentication.user;

    try {
        if (!currentUser) {
            return rejectWithValue("No authenticated dealer");
        }

        const currentPage = page ?? 1;

        if (currentPage < 1) {
            return rejectWithValue("Invalid page number");
        }

        if (fetchedPages.includes(currentPage)) {
            return {
                listings: [],
                page: currentPage,
                totalPages: state.auction.pages || 1,
                total: state.auction.total
            };
        }

        const start = (currentPage - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        const dbResponse = await supabase
            .from("auctions")
            .select(
                "id,vehicle_id,starting_bid,current_bid,reserve_price,ending,status,created_at,vehicle:vehicles!inner(*)",
                {count: "exact"}
            )
            .eq("vehicle.seller_id", currentUser.id)
            .order("created_at", {ascending: false})
            .range(start, end);

        if (dbResponse.error) {
            return rejectWithValue(dbResponse.error.message);
        }

        const totalRows = dbResponse.count ?? 0;
        const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

        if (currentPage > totalPages) {
            return rejectWithValue("Requested page is out of range");
        }

        const mapped = (dbResponse.data ?? []).map((row) => mapDbAuctionToCarAuction(row, currentUser));
        const listings = applyFilters(mapped, filters);

        return {
            listings,
            page: currentPage,
            totalPages,
            total: totalRows
        };
    } catch (error) {
        if (error instanceof Error) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue("An error occurred");
    }
});

export const fetchLatestAuction = createAsyncThunk<
    CarAuction | undefined,
    void,
    { state: RootState; rejectValue: string }
>("auction/fetchLatest", async (_, {getState, rejectWithValue}) => {
    try {
        const currentUser = getState().authentication.user;
        if (!currentUser) {
            return rejectWithValue("No authenticated dealer");
        }
        const dbResponse = await supabase
            .from("auctions")
            .select(
                "id,vehicle_id,starting_bid,current_bid,reserve_price,ending,status,created_at,vehicle:vehicles!inner(*)"
            )
            .eq("vehicle.seller_id", currentUser.id)
            .eq('vehicle.active', 'active')
            .order("created_at", {ascending: false})
            .limit(1)
            .maybeSingle();

        if (dbResponse.error) {
            return rejectWithValue(dbResponse.error.message);
        }

        if (!dbResponse.data) {
            return undefined;
        }

        return mapDbAuctionToCarAuction(dbResponse.data, currentUser);
    } catch (error) {
        if (error instanceof Error) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue("Failed to fetch latest auction");
    }
});

export const fetchTopBidder = createAsyncThunk<
    { auctionId: string; bid: AuctionBid; count: number },
    string,
    { rejectValue: string }
>(
    "auction/fetchTopBidder",
    async (auctionId, {rejectWithValue}) => {
        try {
            const response = await supabase
                .from("auction_bids")
                .select("*, user:profiles(*)", {count: "exact"})
                .eq("auction_id", auctionId)
                .order("created_at", {ascending: false})
                .limit(1);

            if (response.error) {
                return rejectWithValue("Failed to fetch top bidder");
            }

            if (!response.data || response.data.length === 0) {
                return rejectWithValue("Vehicle has no auctions");
            }

            return {
                auctionId,
                bid: keysToCamelCase<AuctionBid>(response.data[0]),
                count: response.count ?? 0
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "failed to fetch top bidder"));
        }
    }
);

export const subscribeToLatestAuctionAsync = createAsyncThunk<
    void,
    void,
    { state: RootState }
>("auction/subscribe", async (_, {getState, dispatch}) => {
    const {currentAuction} = getState().auction;

    if (!currentAuction || mockBidInterval) return;

    mockBidInterval = setInterval(() => {
        const state = getState();
        const auction = state.auction.currentAuction;

        if (!auction) return;

        const lastBidAmount =auction.bids? auction.bids.at(-1)?.amount ?? auction.startingBid : 0;
        const increment = Math.floor(Math.random() * 5000) + 1000;

        const newBid: AuctionBid = {
            id: Date.now().toString(),
            auctionId: auction.auctionId ?? "",
            userId: Math.floor(Math.random() * 1000).toString(),
            user: {
                id: Math.floor(Math.random() * 1000).toString(),
                email: `guest${Math.floor(Math.random() * 1000)}@example.com`,
                firstName: "Guest",
                lastName: `${Math.floor(Math.random() * 1000)}`,
                createdAt: new Date().toISOString(),
                verification: {
                    emailVerified: false,
                    phoneVerified: false,
                    kycVerified: false
                },
                preferences: {
                    currency: "KES",
                    language: "en",
                    notifications: {
                        email: false,
                        sms: false,
                        push: false
                    }
                },
                security: {
                    twoFactorAuth: false,
                    recentLogins: []
                }
            },
            amount: lastBidAmount + increment,
            createdAt: new Date().toISOString(),
            vehicleId: auction.id
        };

        dispatch(
            setCurrentAuction({
                ...auction,
                currentBid: newBid.amount,
                bids: [...(auction.bids ?? []), newBid],
                totalBids: (auction.totalBids ?? auction.bids?.length ?? 0) + 1
            })
        );
    }, 3000);
});

export const unsubscribeFromAuction = createAsyncThunk("auction/unsubscribe", async () => {
    if (mockBidInterval) {
        clearInterval(mockBidInterval);
        mockBidInterval = null;
    }
});

export const setCurrentAuctionAsync = createAsyncThunk<
    CarAuction,
    string,
    { state: RootState; rejectValue: string }
>("auction/setCurrentAuctionAsync", async (id, {getState, rejectWithValue}) => {
    try {
        const auctionState = getState().auction;
        const currentUser = getState().authentication.user;
        if (!currentUser) {
            return rejectWithValue("No authenticated dealer");
        }
        const auction = auctionState.auctions.find((a) => a.id === id);

        if (auction) return auction;

        const [dbResponse, watchCountResponse] = await Promise.all([
            supabase
                .from("auctions")
                .select("id,vehicle_id,starting_bid,current_bid,reserve_price,ending,status,created_at,vehicle:vehicles!inner(*)")
                .eq("vehicle_id", id)
                .eq("vehicle.seller_id", currentUser.id)
                .single(),
            supabase
                .from("vehicle_events")
                .select("*", {count: "exact", head: true})
                .eq("vehicle_id", id)
                .eq("dealer_id", currentUser.id)
                .eq("event_type", "watch")
        ]);

        if (dbResponse.error || !dbResponse.data) {
            return rejectWithValue(dbResponse.error?.message ?? "Auction not found");
        }

        if (watchCountResponse.error) {
            return rejectWithValue(watchCountResponse.error.message);
        }

        return {
            ...mapDbAuctionToCarAuction(dbResponse.data, currentUser),
            watchCount: watchCountResponse.count ?? 0
        };
    } catch (error) {
        if (error instanceof Error) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue("An error occurred");
    }
});

export const auctionSlice = createSlice({
    name: "auction",
    initialState,
    reducers: {
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setError(state, action: PayloadAction<string | undefined>) {
            setErrorState(state, action.payload);
        },
        resetTimer() {
            if (mockBidInterval) {
                clearInterval(mockBidInterval);
            }
            mockBidInterval = null;
        },
        setAuctions(
            state,
            action: PayloadAction<{
                auctions: CarAuction[];
                pageNumber: number;
            }>
        ) {
            const {auctions, pageNumber} = action.payload;

            if (!state.fetchedPages.includes(pageNumber)) {
                state.auctions.push(...auctions);
                state.fetchedPages.push(pageNumber);
            }

            state.pageNumber = pageNumber;
            state.fetched = state.fetchedPages.length > 0;
            state.loading = false;
        },
        addAuction(state, action: PayloadAction<CarAuction>) {
            state.auctions.push(action.payload);
        },
        resetAuctions(state) {
            state.auctions = [];
            state.latestAuction = undefined;
            state.latestAuctionFetched = false;
            state.currentAuction = undefined;
            state.fetchedPages = [];
            state.pageNumber = 1;
            state.pages = 0;
            state.total = 0;
            state.fetched = false;
            setErrorState(state, undefined);
        },
        setLatestAuction(state, action: PayloadAction<CarAuction | undefined>) {
            state.latestAuction = action.payload;
        },
        setCurrentAuction(state, action: PayloadAction<CarAuction | undefined>) {
            state.currentAuction = action.payload;
        },
        setPageSize(state, action: PayloadAction<number>) {
            state.pageSize = action.payload;
        },
        setCurrentPage(state, action: PayloadAction<number>) {
            state.pageNumber = action.payload;
        },
        setAuctionFilters(state, action: PayloadAction<ListingFilters>) {
            state.filters = {
                ...state.filters,
                ...action.payload
            };
            state.pageNumber = 1;
            state.pages = 0;
            state.total = 0;
            state.fetchedPages = [];
            state.auctions = [];
            state.latestAuctionFetched = false;
            state.fetched = false;
        },
        clearAuctionFilters(state) {
            state.filters = {};
            state.pageNumber = 1;
            state.pages = 0;
            state.total = 0;
            state.fetchedPages = [];
            state.auctions = [];
            state.latestAuctionFetched = false;
            state.fetched = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAuctionsAsync.pending, (state) => {
                state.loading = true;
                setErrorState(state, undefined);
            })
            .addCase(fetchAuctionsAsync.fulfilled, (state, action) => {
                state.loading = false;
                setErrorState(state, undefined);

                const {page, totalPages, listings, total} = action.payload;

                state.pageNumber = page;
                state.pages = totalPages;
                state.total = total;

                if (!state.fetchedPages.includes(page)) {
                    state.fetchedPages.push(page);
                    state.auctions.push(...listings);
                }

                state.fetched = state.fetchedPages.length > 0;
            })
            .addCase(fetchAuctionsAsync.rejected, (state, action) => {
                state.loading = false;
                setErrorState(
                    state,
                    (action.payload as string) ?? action.error.message ?? "Failed to fetch auctions"
                );
            })
            .addCase(fetchLatestAuction.pending, (state) => {
                state.loading = true;
                setErrorState(state, undefined);
            })
            .addCase(fetchLatestAuction.fulfilled, (state, action) => {
                state.loading = false;
                setErrorState(state, undefined);
                state.latestAuction = action.payload;
                state.latestAuctionFetched = true;
            })
            .addCase(fetchLatestAuction.rejected, (state, action) => {
                state.loading = false;
                state.latestAuctionFetched = true;
                setErrorState(
                    state,
                    (action.payload as string) ?? action.error.message ?? "Failed to fetch latest auction"
                );
            })
            .addCase(fetchTopBidder.fulfilled, (state, action) => {
                const {auctionId, bid, count} = action.payload;

                if (state.latestAuction?.auctionId === auctionId) {
                    state.latestAuction = {
                        ...state.latestAuction,
                        bids: [bid],
                        totalBids: count,
                        currentBid: bid.amount
                    };
                }

                if (state.currentAuction?.auctionId === auctionId) {
                    state.currentAuction = {
                        ...state.currentAuction,
                        bids: [bid, ...(state.currentAuction.bids ?? []).filter((item) => item.id !== bid.id)].slice(0, 5),
                        totalBids: count,
                        currentBid: bid.amount
                    };
                }

                state.auctions = state.auctions.map((auction) =>
                    auction.auctionId === auctionId
                        ? {
                            ...auction,
                            bids: [bid],
                            totalBids: count,
                            currentBid: bid.amount
                        }
                        : auction
                );
            })
            .addCase(subscribeToLatestAuctionAsync.pending, (state) => {
                state.loading = true;
                setErrorState(state, undefined);
            })
            .addCase(subscribeToLatestAuctionAsync.fulfilled, (state) => {
                state.loading = false;
                state.isSubscribed = true;
            })
            .addCase(subscribeToLatestAuctionAsync.rejected, (state, action) => {
                state.loading = false;
                setErrorState(state, action.error.message ?? "Failed to subscribe to auction");
            })
            .addCase(unsubscribeFromAuction.fulfilled, (state) => {
                state.isSubscribed = false;
            })
            .addCase(setCurrentAuctionAsync.pending, (state) => {
                state.loading = true;
                setErrorState(state, undefined);
            })
            .addCase(setCurrentAuctionAsync.fulfilled, (state, action) => {
                state.loading = false;
                setErrorState(state, undefined);
                state.currentAuction = action.payload;
            })
            .addCase(setCurrentAuctionAsync.rejected, (state, action) => {
                state.loading = false;
                setErrorState(
                    state,
                    (action.payload as string) ?? action.error.message ?? "Failed to find auction"
                );
            })
            .addCase(createVehicleThunk.pending, (state) => {
                state.loading = true;
                setErrorState(state, undefined);
            })
            .addCase(createVehicleThunk.fulfilled, (state) => {
                state.loading = false;
                setErrorState(state, undefined);
            })
            .addCase(createVehicleThunk.rejected, (state, action) => {
                state.loading = false;
                setErrorState(
                    state,
                    (action.payload as string) ?? action.error.message ?? "Failed to create vehicle"
                );
            });
    }
});

export const {
    setLoading,
    setError,
    setAuctions,
    resetAuctions,
    setLatestAuction,
    setCurrentAuction,
    addAuction,
    setPageSize,
    resetTimer,
    setCurrentPage,
    setAuctionFilters,
    clearAuctionFilters
} = auctionSlice.actions;

export default auctionSlice;
